import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class DomainContextService {
  private readonly API_URL = environment.apiUrl + '/v1';
  private readonly STORAGE_KEY = 'taptimize.selectedDomain';

  private initialized = false;
  private readonly domainsSubject = new BehaviorSubject<string[]>([]);
  private readonly selectedDomainSubject = new BehaviorSubject<string>('');
  private readonly loadedSubject = new BehaviorSubject<boolean>(false);

  readonly domains$ = this.domainsSubject.asObservable();
  readonly selectedDomain$ = this.selectedDomainSubject.asObservable();
  readonly loaded$ = this.loadedSubject.asObservable();

  constructor(private http: HttpClient) {}

  get domains(): string[] {
    return this.domainsSubject.value;
  }

  get selectedDomain(): string {
    return this.selectedDomainSubject.value;
  }

  ensureInitialized() {
    if (this.initialized) return;
    this.initialized = true;
    this.loadedSubject.next(false);
    this.loadDomains();
  }

  /** Re-fetch the domain list (e.g. after adding a new domain in Setup). */
  refresh(preferDomain?: string) {
    this.initialized = true;
    this.loadDomains(preferDomain);
  }

  setSelectedDomain(domain: string) {
    const normalized = domain.trim().toLowerCase().replace(/^www\./, '');
    if (normalized === this.selectedDomainSubject.value) return;
    this.selectedDomainSubject.next(normalized);
    if (normalized) {
      localStorage.setItem(this.STORAGE_KEY, normalized);
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  reset() {
    this.initialized = false;
    this.domainsSubject.next([]);
    this.selectedDomainSubject.next('');
    this.loadedSubject.next(false);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  private loadDomains(preferDomain?: string) {
    // Focal scope: the client's own sites, never the rival hosts stored by Compare.
    // `keep` holds a domain an operator just started crawling in Setup.
    const params: Record<string, string> = { scope: 'focal' };
    if (preferDomain) params['keep'] = preferDomain;
    this.http.get<{ success: boolean; data: string[] }>(`${this.API_URL}/urls/domains`, { params }).subscribe({
      next: (res) => {
        const domains = Array.isArray(res.data) ? res.data : [];
        this.domainsSubject.next(domains);

        const wanted = (preferDomain || localStorage.getItem(this.STORAGE_KEY) || '')
          .trim()
          .toLowerCase()
          .replace(/^www\./, '');
        const preferred = domains.includes(wanted) ? wanted : (domains[0] || '');
        this.setSelectedDomain(preferred);
        this.loadedSubject.next(true);
      },
      error: () => {
        this.domainsSubject.next([]);
        this.selectedDomainSubject.next('');
        this.loadedSubject.next(true);
      }
    });
  }
}
